import { updateActiveUsers, removeActiveUsers } from '@editor2/reducers/editor/editorSlice';
import { handleResourceUpdateNotification } from '@editor2/reducers/editor/effects';
import { store } from '@editor2/store';
import { showToast } from '@editor2/reducers/toastSlice';
import { ToastSeverity } from '@editor2/types/toastSeverity';
import { debugLog } from './debugService';

import { getResourceCurrentHead } from './apiService';
import consumer from './consumerService';

export const connectSockets = ({ roomName, courseId }): { unsubscribe: () => void }[] => {
	const room = `editor.course.${courseId}.${roomName}`;

	// Guard against showing duplicate toasts when multiple subscriptions
	// within the same connectSockets call are rejected simultaneously.
	let rejectionToastShown = false;

	const disconnectedOrRejected = function (reason?: { willAttemptReconnect?: boolean }) {
		const splitRoomName = room.split('.');
		const shortRoom = splitRoomName[splitRoomName.length - 1];

		debugLog('websocket', `WebSocket disconnected/rejected for room ${room}`, reason);

		store.dispatch(
			removeActiveUsers({
				courseId,
				room: shortRoom
			})
		);
	};

	const rejected = function () {
		debugLog('websocket', `WebSocket subscription REJECTED for room ${room} — likely an expired or invalid JWT`);

		if (!rejectionToastShown) {
			rejectionToastShown = true;
			store.dispatch(
				showToast({
					severity: ToastSeverity.Error,
					message: 'Real-time connection was rejected (your session may have expired). Please reload the page or re-launch the editor.'
				})
			);
		}

		disconnectedOrRejected();
	};

	const presenceSubscription = consumer.subscriptions.create(
		{
			channel: 'PresenceChannel',
			room: room
		},
		{
			received: function (data) {
				const splitRoomName = data.room.split('.');

				store.dispatch(
					updateActiveUsers({
						courseId,
						room: splitRoomName[splitRoomName.length - 1],
						activeUsers: data.user_names
					})
				);
			},
			disconnected: disconnectedOrRejected,
			rejected: rejected
		}
	);

	const pushNotificationsSubscription = consumer.subscriptions.create(
		{
			channel: 'PushNotificationsChannel',
			rooms: [`course:${courseId}`]
		},
		{
			received: async function (data) {
				if (data.type === 'course.resource.update') {
					const { data: remoteHeadData } = await getResourceCurrentHead(data.course_id);
					handleResourceUpdateNotification(data.course_id, remoteHeadData);
				}
			},
			disconnected: disconnectedOrRejected,
			rejected: rejected
		}
	);

	return [presenceSubscription, pushNotificationsSubscription];
};
